import type { StatusKey } from '@/data/types/contract-status'
import type { WorkspaceArea } from '@/data/types/area'

interface Description {
	'stock-check'?: object
	pick?: object
	journeys?: object
	'load-delivery'?: object
	collections?: object
}

export interface WorkspaceStatus {
	[key: string]: Description
}

export type Status = {
	label: string
	icon: string
	code: number
	status: WorkspaceStatus
}

/**
 * Convert WorkspaceArea to ContractItem area status key
 */
export const contractItemStatusMap: Record<WorkspaceArea, StatusKey> = {
	'stock-check': 'stock_check_status',
	pick: 'pick_status',
	'load-delivery': 'load_status',
	journey: 'journey_status',
	collections: 'collection_status'
}

/**
 * Status and Sub-Status Map
 * Note: status = workspace -> area -> colour: description
 */
export const statusMap: { [key: string]: Status } = {
	'action-required': {
		label: 'Action Required',
		icon: 'action-required',
		code: 0,
		status: {
			'hire-desk': {
				'stock-check': {
					default: 'Send to depot or skip'
				},
				pick: {
					default: 'Trigger pick note'
				}
			}
		}
	},
	'in-progress': {
		label: 'In Progress',
		icon: 'in-progress',
		code: 1,
		status: {
			'hire-desk': {
				'stock-check': {
					default: 'No action required',
					'in-progress-warning': 'Items still to action',
					'in-progress-complete': 'Items complete'
				},
				pick: {
					default: 'Pick note triggered and with depot',
					load: 'Load has been sent to Yard Workspace'
				},
				journeys: {
					default: 'Journey created'
				}
			},
			yard: {
				'stock-check': {
					default: 'Complete stock check or assign to Yard Operator',
					'in-progress-warning': 'Items still to action',
					'in-progress-complete': 'Items complete'
				},
				pick: {
					default: 'Assign pick to yard operator'
				}
			}
		}
	},
	assigned: {
		label: 'Assigned',
		icon: 'assigned',
		code: 2,
		status: {
			'hire-desk': {
				'stock-check': {
					default: 'Depot has assigned for checking'
				},
				pick: {
					default: 'Depot has assigned for picking',
					load: 'Load has been assigned to Yard Tablet'
				}
			},
			yard: {
				'stock-check': {
					default: 'Stock check with yard operator'
				},
				pick: {
					default: 'Pick with yard operator'
				}
			}
		}
	},
	warning: {
		label: 'Warning',
		icon: 'warning',
		code: 3,
		status: {
			'hire-desk': {
				'stock-check': {
					default: 'Lines need attention (shortages or notes)',
					'pick-warning': 'Pick has failed'
				},
				pick: {
					default: 'Lines need attention (shortages or notes)',
					'pick-warning': 'Pick has failed',
					load: 'Issue with Load that needs to be resolved'
				}
			},
			yard: {
				'stock-check': {
					default: 'Lines need attention (shortages or notes)',
					'pick-warning': 'Pick has failed'
				},
				pick: {
					default: 'Lines need attention (shortages or notes)',
					'pick-warning': 'Pick has failed'
				}
			}
		}
	},
	complete: {
		label: 'Complete',
		icon: 'complete',
		code: 4,
		status: {
			'hire-desk': {
				'stock-check': {
					default: 'All items fully stocked - checked OK'
				},
				pick: {
					default: 'All items fully picked and ready for allocation on inspHire',
					load: 'Load is complete and ready to be allocated'
				}
			}
		}
	},
	skipped: {
		label: 'Skipped',
		icon: 'skipped',
		code: 5,
		status: {
			'hire-desk': {
				'stock-check': {
					default: 'Stock check skipped'
				}
			},
			yard: {
				journeys: {
					default: 'Journey cancelled - new journey required'
				}
			}
		}
	},
	'in-transit': {
		label: 'In Transit',
		icon: 'in-transit',
		code: 6,
		status: {
			'hire-desk': {
				journeys: {
					default: 'Journey activated in transit'
				}
			},
			yard: {
				journeys: {
					default: 'Journey activated in transit'
				}
			}
		}
	},
	delivered: {
		label: 'Delivered',
		icon: 'delivered',
		code: 7,
		status: {
			yard: {
				journeys: {
					default: 'Journey complete'
				}
			}
		}
	}
}
